/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Button, Modal } from '@scuf/common';
import React, { useContext, useEffect, useState } from 'react';
import './MeterDataUploadCommon.scss';
import {
  meterDatamaxSizeCheck,
  meterDataminSizeCheck,
  uploadTypeCheck
} from '../MeterDataUploadModal/UploadTab/UpdatedUploadTab';
import { useFileUploadContext } from '../../../context/FileUploadContext/FileUploadContext';
import FileUploadAction from '../../../context/FileUploadContext/FileUploadActions';
import descriptors from '../../../locales/en-US/descriptors';
import { useTranslation } from '@scuf/localization';
import { ApiContext } from '../../../context/ApiContext';
import { isUploadDisabled } from '../../shared/Utils/Utils';
import {
  POLLING_MILLI_SECONDS,
  PUBLISH_MODAL_REDIRECT_TIME,
  UpdatedManualFileUploadTypeList,
  UPLOAD_MODAL_REDIRECT_TIME
} from '../../shared/Utils/Constants';
import UpdatedChooseDataTypeTab from './ChooseDataTypeSection/UpdatedChooseDataTypeTab';
import '../MeterDataUploadModal/ChooseDataTypeSection/UpdatedChooseDataTypeThemes.scss';
import {
  IAny,
  IChunkedUploadProgressState
} from '../../DashboardKPIWidgets/IEnergyManagementProps';

const Indexes = {
  ChooseDataTypeIndex: 0,
  UploadTabIndex: 1,
  LoadingTabIndex: 2
};
function UpdatedUploadModal(props: {
  startUpload: (file: File[]) => Promise<IAny>;
}) {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const [uploadDisabled, setUploadDisabled] = useState(true);
  const [state, dispatch] = useFileUploadContext();
  const { showModal, acceptedFiles, acceptedCsvHeaders } = state;
  const [ActiveIndex, setActiveIndex] = useState(Indexes.ChooseDataTypeIndex);
  const [SelectedDataTye, setSelectedDataTye] = useState(
    UpdatedManualFileUploadTypeList[0].type
  );
  const [redirectSeconds, setRedirectSeconds] = useState(
    UPLOAD_MODAL_REDIRECT_TIME
  );
  const [dataError, setDataError] = useState();
  const [progress, setProgress] = useState(0);

  const setUploadDisableonFileError = (err: IAny) => {
    setDataError(err);
  };

  const minSize = meterDataminSizeCheck(SelectedDataTye);
  const maxSize = meterDatamaxSizeCheck(SelectedDataTye);

  const apiContext = useContext(ApiContext);
  const { progressTrackingList } = apiContext;
  const closeModal = () => {
    setActiveIndex(Indexes.ChooseDataTypeIndex);
    dispatch(FileUploadAction.hideUploadModal(false));
  };
  const renderActivetab = (tabIndex: number) => {
    switch (tabIndex) {
      case Indexes.ChooseDataTypeIndex:
        return renderChooseDataTypeTabContent();
      case Indexes.LoadingTabIndex:
        return renderLoadingStatus();
      default:
        return renderChooseDataTypeTabContent();
    }
  };
  const handleDataTypeChange = (selectedDataType: string) => {
    setSelectedDataTye(selectedDataType);
  };
  const renderChooseDataTypeTabContent = () => {
    return (
      <>
        <UpdatedChooseDataTypeTab
          selectedDataType={SelectedDataTye}
          onSelectDataType={handleDataTypeChange}
          setUploadDisabled={setUploadDisabled}
          dataError={dataError}
          setUploadDisableonFileError={setUploadDisableonFileError}
        />
        {renderFooterButtons()}
      </>
    );
  };
  const startTimer = () => {
    const reduceCountTime = POLLING_MILLI_SECONDS;
    let timer = setTimeout(() => {
      if (redirectSeconds === 0) {
        setRedirectSeconds(PUBLISH_MODAL_REDIRECT_TIME);
        clearTimeout(timer);
        setActiveIndex(Indexes.ChooseDataTypeIndex);
        closeModal();
      } else {
        setRedirectSeconds(redirectSeconds - 1);
      }
    }, reduceCountTime);
  };

  useEffect(() => {
    isUploadDisabled(acceptedFiles, minSize, maxSize, acceptedCsvHeaders).then(
      (isDisabled) => {
        const f = async () => {
          let totalSize = acceptedFiles.reduce(
            (acc: number, file: File) => acc + file.size,
            0
          );
          if (progressTrackingList)
            totalSize += progressTrackingList.reduce(
              (acc: number, file: IChunkedUploadProgressState) =>
                acc + (file.removed != true ? file.fileSize : 0),
              0
            );
          const enoughSpace = totalSize <= maxSize;
          setUploadDisabled(!enoughSpace || isDisabled);
        };
        f();
      }
    );
  }, [acceptedFiles, progressTrackingList]);

  const renderLoadingStatus = () => {
    startTimer();
    return (
      <div className="loading-status">
        <div>
          <div className="align-center">
            <Button
              className="upload-modal-loading"
              loading={true}
              disabled={true}
              content="Loading"
            />
          </div>
          <div className="align-center f-20">
            {t(d.UPLOADING)} {acceptedFiles.length} {t(d.FILES)}{' '}
          </div>
          <div className="align-center pt-10">{t(d.UPLOADINGMESSAGE1)}</div>
          <hr className="hr-width mtb-20" />
          <div className="align-center">
            {t(d.REDIRECTMESSAGE1)} {redirectSeconds} {t(d.REDIRECTMESSAGE2)}
          </div>
        </div>
      </div>
    );
  };
  const renderFooterButtons = () => {
    return (
      <div className="upload-modal-footer">
        <div>
          <Button
            className="upload-data-cancel upload-footer-button"
            type="secondary"
            onClick={() => {
              closeModal();
            }}
          >
            {t(d.BACK)}
          </Button>
        </div>
        <div>
          <Button
            disabled={uploadDisabled}
            style={{ marginLeft: 'auto' }}
            type="primary"
            onClick={() => {
              setActiveIndex(Indexes.LoadingTabIndex);
              onClickSumbitUpload();
            }}
          >
            {t(d.UPLOADFILE)}
          </Button>
        </div>
      </div>
    );
  };
  const onClickSumbitUpload = async () => {
    if (acceptedFiles.length > 0) {
      try {
        await props.startUpload(acceptedFiles);
        setUploadDisabled(true);
      } catch (error) {
        console.log(error);
      } finally {
        setUploadDisabled(false);
      }
    }
  };

  return (
    <React.Fragment>
      <Modal
        className="meter-data-upload-modal"
        size="large"
        closeIcon={ActiveIndex === Indexes.LoadingTabIndex ? false : true}
        onClose={closeModal}
        open={showModal}
        closeOnDimmerClick={false}
      >
        <div className="meter-data-upload-modal-header">
          {t(d.UPLOADMETERDATA)}
        </div>
        <Modal.Content className="meter-data-upload-modal-content">
          {renderActivetab(ActiveIndex)}
        </Modal.Content>
      </Modal>
    </React.Fragment>
  );
}

export default UpdatedUploadModal;
